import type { JSONContent } from '@sequencehq/core-components'
import type {
  QuoteBlock,
  TableBlockCell,
  TableBlockConfig
} from '@sequencehq/quote-content'
import { textContentAdapter } from './textContent'
import { alignmentIn } from './textBlockAttributes'

const EMPTY_CELL: TableBlockCell = {
  text: [],
  textAlignment: 'left',
  textColor: 'default',
  backgroundColor: 'default'
}

/**
 * Converts between saved tables and the editor's. The saved column widths are
 * the first row's, and a column that was never resized is saved without one.
 */
export const tableAdapter = {
  /** A table without any cells has nothing to show, so it's dropped. */
  in: (block: QuoteBlock): JSONContent[] => {
    const config = block.config as TableBlockConfig
    const columnCount = Math.max(0, ...config.rows.map(row => row.cells.length))

    if (columnCount === 0) {
      return []
    }

    return [
      {
        type: 'table',
        attrs: {
          textColor: config.textColor ?? 'default',
          headerRow: Boolean(config.headerRow),
          headerColumn: Boolean(config.headerColumn)
        },
        content: config.rows.map(row => ({
          type: 'tableRow',
          content: padCells(row.cells, columnCount).map((cell, index) =>
            cellIn(cell, config.columnWidths?.at(index))
          )
        }))
      }
    ]
  },
  out: (node: JSONContent): QuoteBlock => {
    const rows = node.content ?? []

    return {
      blockType: 'table',
      config: {
        textColor: node.attrs?.textColor ?? 'default',
        /**
         * Saved as `null`, as BlockNote did, though the type only allows
         * `undefined`.
         */
        columnWidths: (rows.at(0)?.content ?? []).map(
          cell => cell.attrs?.colwidth?.at(0) ?? null
        ) as TableBlockConfig['columnWidths'],
        headerRow: Boolean(node.attrs?.headerRow),
        headerColumn: Boolean(node.attrs?.headerColumn),
        rows: rows.map(row => ({ cells: (row.content ?? []).map(cellOut) }))
      },
      version: 1
    }
  }
}

/**
 * The editor needs every row to have the same number of cells, so a short row
 * is filled out with empty cells.
 */
const padCells = (cells: TableBlockCell[], columnCount: number) => [
  ...cells,
  ...Array.from({ length: columnCount - cells.length }, () => EMPTY_CELL)
]

/**
 * Each cell in a column takes the column's width, so the editor doesn't treat
 * the cells below the first row as mismatched and rewrite them.
 */
const cellIn = (
  cell: TableBlockCell,
  width: number | null | undefined
): JSONContent => ({
  type: 'tableCell',
  attrs: {
    colwidth: width ? [width] : null,
    textColor: cell.textColor ?? 'default',
    backgroundColor: cell.backgroundColor ?? 'default'
  },
  content: [
    {
      type: 'paragraph',
      attrs: { textAlign: alignmentIn(cell.textAlignment) },
      content: textContentAdapter.in(cell.text)
    }
  ]
})

const cellOut = (cell: JSONContent): TableBlockCell => {
  const paragraph = cell.content?.at(0)

  return {
    text: textContentAdapter.out(paragraph?.content),
    textAlignment: paragraph?.attrs?.textAlign ?? 'left',
    textColor: cell.attrs?.textColor ?? 'default',
    backgroundColor: cell.attrs?.backgroundColor ?? 'default'
  }
}
